import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DestinationEndpoint } from '../../../models/destination-endpoint';
import { ImportType } from '../../../models/import-type';
import { SearchTerm } from '../../../models/search-term';
import { ReportService } from 'src/app/services/report.service';
import { ToastService } from 'src/app/services/toast.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { ReportConstants } from 'src/app/models/report-constants';
import { ReportingUtils } from 'src/app/utilities/reporting-utilities';
import { BaseEndpointMapping } from 'src/app/models/base-endpoint-mapping';
import { BaseEndpointMappingType } from 'src/app/models/base-endpoint-mapping-type';
import { ColumnSettings } from 'src/app/models/column-settings';
import { BaseMapping } from 'src/app/models/base-mapping';
import { GenericEndpoint } from 'src/app/models/generic-endpoint';
import { LTGenericGridWrapperComponent } from 'src/app/features/lt-shared-ui/lt-grid-ui/components/lt-generic-grid-wrapper/lt-generic-grid-wrapper';
const _flatten = filter => {
  const filters = filter.filters;
  if (filters) {
    return filters.reduce((acc, curr) => acc.concat(curr.filters ? _flatten(curr) : [curr]), []);
  }
  return [];
};

@Component({
  selector: 'app-mappings-report-view-v2',
  templateUrl: './mappings-report-view-v2.component.html',
  styleUrls: ['./mappings-report-view-v2.component.scss'],
  providers: [ReportService, TypeConstantService],
  standalone: false,
})
export class MappingsReportViewV2Component implements OnInit, OnDestroy {
  @ViewChild('viewGenericGrid') public viewGenericGrid: LTGenericGridWrapperComponent;
  
  componentDestroyed$ = new Subject<boolean>();
  reportingUtils: ReportingUtils;
  reportConstants: ReportConstants;

  allBaseMappingData: BaseMapping[] = [];
  allGenericEndpointData: GenericEndpoint[] = [];
  allBaseEndpointMappingData: BaseEndpointMapping[] = [];

  filteredBaseMappingData: BaseMapping[] = [];
  filteredGenericEndpointData: GenericEndpoint[] = [];
  filteredBaseEndpointMappingData: BaseEndpointMapping[] = [];
  destinationEndpointItems: DestinationEndpoint[] = [];
  importTypeItems: ImportType[] = [];

  // current mapping type for report and data
  currMappingType: BaseEndpointMappingType = BaseEndpointMappingType.BaseEndpointMapping;

  public mappingType = BaseEndpointMappingType;

  reportColumns: ColumnSettings[] = [];
  reportMappingData: any;

  // filter and search variables
  filtersChanged = false;
  xmlSearchTerm: SearchTerm;
  jsSearchTerm: SearchTerm;

  // filter cards data
  destinationEndpointItemsSelected: DestinationEndpoint[] = []; // csd.. lg..
  importTypeItemsSelected: ImportType[] = [];

  showMappingContainer = false;
  haveMappings = false;
  gettingMappings = false;

  constructor(
    protected reportService: ReportService,
    protected toastService: ToastService,
    protected typeService: TypeConstantService,
    private location: Location,
    private router: Router,
  ) {
    this.reportingUtils = new ReportingUtils();
  }

  ngOnInit(): void {
    this.setUpReportData();
  }

  setUpReportData(): void {
    this.xmlSearchTerm = new SearchTerm('xmlConfig', 'Search XML Config...');
    this.jsSearchTerm = new SearchTerm('', 'Search JS...');
    this.currMappingType = this.reportingUtils.getBaseEndpointMappingTypeByString('BaseEndpointMapping');
    this.reportColumns = this.reportingUtils.getDefaultColumns(this.currMappingType);

    this.getBaseEndpointMappingDataForReport();
    this.typeService.getAllDestinationEndpoints()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (endpoints) => {
          this.destinationEndpointItems = endpoints;
          if (this.destinationEndpointItems.length > 0) {
            this.destinationEndpointItems.sort((a, b) => a.endpointName.localeCompare(b.endpointName));
          }
        }, (_error) => {
          this.toastService.toastCreate('Error getting Destination Endpoints.', 'Warning');
        });
    this.typeService.getAllImportTypes()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (importTypes) => {
          this.importTypeItems = importTypes;
          if (this.importTypeItems.length > 0) {
            this.importTypeItems.sort((a, b) => a.typeDescription.localeCompare(b.typeDescription));
          }
        }, (_error) => {
          this.toastService.toastCreate('Error getting Import Types.', 'Warning');
        });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  public getBaseEndpointMappingDataForReport() : void {
    this.allBaseEndpointMappingData = [];
    this.showMappingContainer = true;
    this.haveMappings = false;
    this.gettingMappings = true;
    this.reportService
      .getAllBaseEndpointMappings()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (data) => {
          if (data != null && data.length > 0) {
            const upd: BaseEndpointMapping[] = data.map(x => new BaseEndpointMapping(x));
            this.allBaseEndpointMappingData = upd;
            this.filteredBaseEndpointMappingData = upd;
            this.gettingMappings = false;
            this.haveMappings = true;
          }
          else {
            this.gettingMappings = false;
            this.haveMappings = false;
          }
        }, (_error) => {
          // console.log(error);
          this.toastService.toastCreate('Error getting mappings.', 'Warning');
        },
      );
  }

  // reset filters
  public resetFilters(): void {
    this.destinationEndpointItemsSelected = [];
    this.importTypeItemsSelected = [];
    this.updateFilteredData();
  }

  // apply filters
  public applyFilters(): void {
    this.updateFilteredData();
  }

  // update Filtered Data - Base Mapping
  public updateFilteredDataForBaseMapping() {
    if (this.currMappingType == BaseEndpointMappingType.BaseMapping) {
      this.filteredBaseMappingData = [];
      this.filteredBaseMappingData = this.allBaseMappingData;

    // filter by search
    if (this.xmlSearchTerm.hasSearchTerm) {
      this.filteredBaseMappingData = this.filteredBaseMappingData.filter(integration => integration.xmlConfig?.toLowerCase()?.includes(this.xmlSearchTerm?.currentSearchValue?.toLowerCase()));
    }
    if (this.jsSearchTerm.hasSearchTerm) {
      this.filteredBaseMappingData = this.filteredBaseMappingData.filter(integration => integration.javaScript?.toLowerCase()?.includes(this.jsSearchTerm?.currentSearchValue?.toLowerCase()));
    }

      // filter by filter cards
      // destination endpoint
      if (this.destinationEndpointItemsSelected.length > 0
        && this.destinationEndpointItemsSelected.length != this.destinationEndpointItems.length) {
          const ids = this.destinationEndpointItemsSelected.map(i => i.destinationEndpointId);
          this.filteredBaseMappingData = this.filteredBaseMappingData.filter(
            i => ids.indexOf(i.destinationEndpointId) != -1,
          );
      }
      // import type ids
      if (this.importTypeItemsSelected.length > 0
        && this.importTypeItemsSelected.length != this.importTypeItems.length) {
        const ids = this.importTypeItemsSelected.map(i => i.importTypeId);
        this.filteredBaseMappingData = this.filteredBaseMappingData.filter(
          i => ids.indexOf(i.importTypeId) != -1,
        );
      }
    }
  }

  // update Filtered Data - Generic Endpoint
  public updateFilteredDataForGenericEndpoint() {
    if (this.currMappingType == BaseEndpointMappingType.GenericEndpoint){
      this.filteredGenericEndpointData = [];
      this.filteredGenericEndpointData = this.allGenericEndpointData;

      
      if (this.xmlSearchTerm.hasSearchTerm) {
        this.filteredGenericEndpointData = this.filteredGenericEndpointData.filter(integration => integration.xmlConfiguration?.toLowerCase()?.includes(this.xmlSearchTerm?.currentSearchValue?.toLowerCase()));
      }
      if (this.jsSearchTerm.hasSearchTerm) {
        this.filteredGenericEndpointData = this.filteredGenericEndpointData.filter(integration => integration.javaScript?.toLowerCase()?.includes(this.jsSearchTerm?.currentSearchValue?.toLowerCase()));
      }

      // filter by filter cards
      // destination endpoint
      if (this.destinationEndpointItemsSelected.length > 0
        && this.destinationEndpointItemsSelected.length != this.destinationEndpointItems.length) {
          const ids = this.destinationEndpointItemsSelected.map(i => i.destinationEndpointId);
          this.filteredGenericEndpointData = this.filteredGenericEndpointData.filter(
            i => ids.indexOf(i.destinationId) != -1,
          );
      }
      // import type ids
      if (this.importTypeItemsSelected.length > 0
        && this.importTypeItemsSelected.length != this.importTypeItems.length) {
        const ids = this.importTypeItemsSelected.map(i => i.importTypeId);
        this.filteredGenericEndpointData = this.filteredGenericEndpointData.filter(
          i => ids.indexOf(i.importTypeId) != -1,
        );
      }
    }
  }

  // update Filtered Data - Base Endpoint Mapping
  public updateFilteredDataForBaseEndpointMapping() {
    if (this.currMappingType == BaseEndpointMappingType.BaseEndpointMapping){
      // reset filtered data
      this.filteredBaseEndpointMappingData = [];
      this.filteredBaseEndpointMappingData = this.allBaseEndpointMappingData;

      
      // filter by search
      if (this.xmlSearchTerm.hasSearchTerm) {
        this.filteredBaseEndpointMappingData = this.filteredBaseEndpointMappingData.filter(integration => integration.xmlConfig?.toLowerCase()?.includes(this.xmlSearchTerm?.currentSearchValue?.toLowerCase()));
      }
      if (this.jsSearchTerm.hasSearchTerm) {
        this.filteredBaseEndpointMappingData = this.filteredBaseEndpointMappingData.filter(integration => integration.javaScript?.toLowerCase()?.includes(this.jsSearchTerm?.currentSearchValue?.toLowerCase()));
      }

      // filter by filter cards
      // destination endpoint
      if (this.destinationEndpointItemsSelected.length > 0
        && this.destinationEndpointItemsSelected.length != this.destinationEndpointItems.length) {
          const ids = this.destinationEndpointItemsSelected.map(i => i.destinationEndpointId);
          this.filteredBaseEndpointMappingData = this.filteredBaseEndpointMappingData.filter(
            i => ids.indexOf(i.destinationEndpointId) != -1,
          );
      }
      // import type ids
      if (this.importTypeItemsSelected.length > 0
        && this.importTypeItemsSelected.length != this.importTypeItems.length) {
        const ids = this.importTypeItemsSelected.map(i => i.importTypeId);
        this.filteredBaseEndpointMappingData = this.filteredBaseEndpointMappingData.filter(
          i => ids.indexOf(i.importTypeId) != -1,
        );
      }
    }
  }

  // update Filtered Data - All
  public updateFilteredData() {
    if (this.currMappingType == BaseEndpointMappingType.BaseMapping) {
      this.updateFilteredDataForBaseMapping();
    }
    else if (this.currMappingType == BaseEndpointMappingType.GenericEndpoint){
      this.updateFilteredDataForGenericEndpoint();
    }
    else if (this.currMappingType == BaseEndpointMappingType.BaseEndpointMapping){
      this.updateFilteredDataForBaseEndpointMapping();
    }
  }

  // SEARCH TERM FUNCTIONS
  clearSearch(item: SearchTerm) {
    item.clear();
    this.updateFilteredData();
    this.viewGenericGrid.resetSkipIndex();
  }

  updateSearch(item: SearchTerm) {
    item.save();
    this.updateFilteredData();
    this.viewGenericGrid.resetSkipIndex();
  }

  public externalFilterChange(): void {
    //console.log("externalFilterChange", value);
  }

}
